import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { PLAN_LABELS } from '../../utils/rbac';

const fallbackContainer = 'glass-card p-6';
const badgeClass =
  'glass-badge border-white/15 bg-white/10 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-200';
const primaryButton =
  'glass-cta w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed';
const mutedButton =
  'glass-ghost w-full text-sm px-4 py-3 font-semibold disabled:opacity-60 disabled:cursor-not-allowed';

function formatPrice(cents, currency = 'USD') {
  if (typeof cents !== 'number') return 'Contact sales';
  const amount = cents / 100;
  try {
    return `${new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: amount >= 1000 ? 0 : 2,
    }).format(amount)}/mo`;
  } catch (err) {
    return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`;
  }
}

function planCtaLabel(plan, busyCode) {
  if (busyCode === plan.code) return 'Processing...';
  if (plan.action === 'current') return 'Current plan';
  if (plan.action === 'upgrade') return `Upgrade to ${plan.name}`;
  if (plan.action === 'downgrade') return `Downgrade to ${plan.name}`;
  return 'Contact support';
}

export default function PlanManager({ onPlanChange, className = '' }) {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState('');
  const [pendingPlan, setPendingPlan] = useState(null);
  const [pendingPlanEffectiveAt, setPendingPlanEffectiveAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [busyCode, setBusyCode] = useState('');

  const containerClass = className || fallbackContainer;

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/billing/plans');
      setPlans(data?.plans || []);
      setCurrentPlan(data?.plan || '');
      setPendingPlan(data?.pendingPlan || null);
      setPendingPlanEffectiveAt(data?.pendingPlanEffectiveAt || null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to load plan catalog.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const pendingCopy = useMemo(() => {
    if (!pendingPlan) return null;
    const label = PLAN_LABELS[pendingPlan] || pendingPlan;
    const timestamp = pendingPlanEffectiveAt ? new Date(pendingPlanEffectiveAt) : null;
    const formatted = timestamp && !Number.isNaN(timestamp.getTime()) ? timestamp.toLocaleString() : null;
    return `Upgrade to ${label} pending${formatted ? ` (started ${formatted})` : ''}. We'll refresh automatically once Stripe confirms payment.`;
  }, [pendingPlan, pendingPlanEffectiveAt]);

  const handleChangePlan = useCallback(
    async (plan) => {
      if (!plan || plan.action === 'locked' || plan.action === 'current') return;
      setBusyCode(plan.code);
      setError('');
      setStatus('');
      try {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const adminBase = origin ? `${origin}/admin` : undefined;
        const payload = {
          targetPlan: plan.code,
          interval: 'monthly',
          successUrl: adminBase ? `${adminBase}?billing=success` : undefined,
          cancelUrl: adminBase ? `${adminBase}?billing=cancel` : undefined,
        };
        const { data } = await api.post('/billing/change-plan', payload);
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        setStatus(
          data?.message ||
            (plan.action === 'downgrade'
              ? 'Plan updated. Refresh to see the latest seat and credit limits.'
              : 'Plan request sent. We will update this page after Stripe confirms the upgrade.')
        );
        await loadPlans();
        if (typeof onPlanChange === 'function') {
          onPlanChange();
        }
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Unable to change plan.');
      } finally {
        setBusyCode('');
      }
    },
    [loadPlans, onPlanChange]
  );

  return (
    <section className={`${containerClass} space-y-4`}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-sm uppercase tracking-wide text-zinc-400">
          <span>Subscription & Billing</span>
          {currentPlan && (
            <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-xs font-semibold text-emerald-200">
              Current: {PLAN_LABELS[currentPlan] || currentPlan}
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-400">
          Choose the plan that fits your building goals. Switch anytime without leaving the app.
        </p>
      </div>

      {pendingCopy && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {pendingCopy}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}

      {status && !error && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          {status}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-400">Loading plans...</p>
      ) : plans.length === 0 ? (
        <p className="text-sm text-zinc-400">No public plans are configured yet.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {plans.map((plan) => {
            const isCurrent = plan.action === 'current';
            const buttonDisabled = plan.action === 'locked' || busyCode === plan.code;
            const bulletItems = Array.isArray(plan.bullets)
              ? (plan.bullets || []).filter((item) => !/seat/i.test(item || ''))
              : [];
            let priceMonthlyCents = plan.priceMonthlyCents;
            if (!priceMonthlyCents) {
              if (plan.code === 'starter') priceMonthlyCents = 2900;
              else if (plan.code === 'pro') priceMonthlyCents = 5900;
            }
            if (plan.code === 'enterprise' && !bulletItems.some((item) => /admin console/i.test(item || ''))) {
              bulletItems.push('Includes admin console for centralized control');
            }
            return (
              <div
                key={plan.code}
                className={`flex h-full flex-col gap-4 rounded-2xl border p-5 ${
                  isCurrent ? 'border-amber-400/50 bg-amber-400/5' : 'border-white/10 bg-white/5'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-zinc-400">{plan.name}</p>
                    <p className="text-2xl font-semibold text-white">
                      {priceMonthlyCents ? formatPrice(priceMonthlyCents, plan.currency || 'usd') : 'Contact sales'}
                    </p>
                  </div>
                  {plan.badge && <span className={badgeClass}>{plan.badge}</span>}
                </div>
                <p className="text-sm text-zinc-400">{plan.tagline}</p>
                {plan.monthlyCredits ? (
                  <div className="text-xs text-zinc-500">
                    {Number(plan.monthlyCredits).toLocaleString()} credits / month
                  </div>
                ) : null}
                <ul className="flex flex-1 flex-col gap-1 text-sm text-zinc-300">
                  {bulletItems.map((bullet, index) => (
                    <li key={`${plan.code}-${index}`} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div>
                  <button
                    type="button"
                    className={plan.action === 'locked' || plan.action === 'current' ? mutedButton : primaryButton}
                    disabled={buttonDisabled}
                    onClick={() => handleChangePlan(plan)}
                  >
                    {planCtaLabel(plan, busyCode)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
